// Pure helpers of the Setup email screen and the domain banner (see views/coaching-portal/custom-workflows)

const EMAIL_PATTERN = /^[^\s@]+@[^.\s@]+(?:\.[^.\s@]+)+$/;

// Addresses at these cannot be authenticated by their user: the domain is not theirs
const FREE_MAILBOX_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "yahoo.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
]);

/**
 * Only the format of an optional email field (the Reply-to).
 * @returns {"invalid"|null}
 */
export const emailFormatError = (email) => {
  const value = (email ?? "").trim();
  return value && !EMAIL_PATTERN.test(value) ? "invalid" : null;
};

export const domainOf = (email) => email.split("@").at(-1).toLowerCase();

/**
 * What is wrong with the From address, as a key of `emailSending.errors`. An empty field has no
 * error (it is optional); once the account has a domain the address has to be at it.
 * @param {string} email
 * @param {string|null} domain the account's domain, when it has one
 * @returns {"invalid"|"freeProvider"|"otherDomain"|null}
 */
export const fromAddressError = (email, domain) => {
  const value = (email ?? "").trim();
  if (!value) return null;
  if (!EMAIL_PATTERN.test(value)) return "invalid";
  if (domain) return domainOf(value) === domain ? null : "otherDomain";
  return FREE_MAILBOX_DOMAINS.has(domainOf(value)) ? "freeProvider" : null;
};

const PURPOSE_KEYS = {
  return_path: "returnPath",
  dkim1: "dkim",
  dkim2: "dkim",
};

/**
 * The rows of the DNS table: the records the API returned plus the DMARC one, each with a
 * `purposeKey` (of `emailSending.records`) and a `status` (verified, waiting, alreadySet or
 * recommended).
 * @param {{records?: Array<object>, dmarc?: {status: string, type: string, host: string, data: string}|null}} settings
 */
export const buildRecordRows = (settings) => {
  const rows = (settings?.records ?? []).map((record) => ({
    key: `${record.purpose}-${record.host}`,
    purposeKey: PURPOSE_KEYS[record.purpose] ?? "dkim",
    type: record.type,
    host: record.host,
    value: record.value,
    status: record.valid ? "verified" : "waiting",
  }));
  const dmarc = settings?.dmarc;
  if (dmarc) {
    rows.push({
      key: "dmarc",
      purposeKey: "dmarc",
      type: dmarc.type,
      host: dmarc.host,
      value: dmarc.data,
      status: dmarc.status === "present" ? "alreadySet" : "recommended",
    });
  }
  return rows;
};

/**
 * Which banner the domain gets: paused (too many bounces or complaints), none (no domain yet),
 * pending (set up, not verified), failed (it was verified and broke) or verified.
 * @returns {"paused"|"none"|"pending"|"failed"|"verified"|null}
 */
export const bannerState = (settings) => {
  if (!settings) return null;
  if (settings.paused) return "paused";
  return settings.state;
};
